import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import logo from "../assets/img/CheckIDBioSense_light.svg";
import { SendButton } from "../components/button/SendButton";
import { fetchCsrfToken } from "../utils/api";

const UploadLogin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isDisabled, setIsDisabled] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);

    useEffect(() => {
        axios
            .get("/uploads/session", { withCredentials: true })
            .then((res) => {
                if (res.data?.authenticated) {
                    navigate("/uploads", { replace: true });
                }
            })
            .catch(() => {})
            .finally(() => setCheckingSession(false));
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password) return;

        setIsDisabled(true);
        try {
            await fetchCsrfToken();
            const csrfRes = await axios.get("/api/contact", { withCredentials: true });
            const token = csrfRes.headers["x-csrf-token"]?.split(",")[0];
            if (!token) {
                toast.error(t("uploads.login.fail"));
                return;
            }

            const formData = new FormData();
            formData.append("username", username.trim());
            formData.append("password", password);

            const res = await axios.post("/uploads/login", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "X-CSRF-TOKEN": token,
                },
                withCredentials: true,
            });

            if (res.data?.success) {
                toast.success(t("uploads.login.success"));
                navigate("/uploads", { replace: true });
            } else {
                toast.error(t("uploads.login.fail"));
            }
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error(t("uploads.login.invalidCredentials"));
            } else {
                toast.error(t("uploads.login.fail"));
            }
        } finally {
            setIsDisabled(false);
        }
    };

    if (checkingSession) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <p className="text-[#3A505F] text-sm">{t("uploads.login.checking")}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-10 md:py-20 bg-gray-50 overflow-y-auto">
            <div className="w-full max-w-md text-center">
                <img
                    src={logo}
                    className="h-[80px] md:h-[90px] mx-auto mb-8 object-contain"
                    alt="CheckID BioSense"
                />

                <h1 className="text-[#1C2045] text-2xl md:text-3xl font-extrabold mb-2">
                    <span className="inline-block border-b-[6px] border-[var(--tree-poppy)] pb-1">
                        {t("uploads.login.title")}
                    </span>
                </h1>
                <p className="text-[#3A505F] text-sm md:text-base mb-8 max-w-sm mx-auto leading-relaxed">
                    {t("uploads.login.description")}
                </p>

                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 text-left">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="flex justify-center mb-2">
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center shadow-md border-4 border-white"
                                style={{
                                    background: "linear-gradient(135deg, var(--tree-poppy)22, var(--tree-poppy)44)",
                                }}
                            >
                                <LockOutlinedIcon sx={{ fontSize: 32, color: "var(--tree-poppy)" }} />
                            </div>
                        </div>

                        <div>
                            <label
                                htmlFor="upload-username"
                                className="block text-sm font-semibold text-[#1C2045] mb-2"
                            >
                                {t("uploads.login.username")}
                            </label>
                            <input
                                id="upload-username"
                                type="text"
                                autoComplete="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={isDisabled}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[var(--tree-leaf)] focus:outline-none text-[#1C2045] disabled:opacity-60"
                                placeholder={t("uploads.login.usernamePlaceholder")}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="upload-password"
                                className="block text-sm font-semibold text-[#1C2045] mb-2"
                            >
                                {t("uploads.login.password")}
                            </label>
                            <input
                                id="upload-password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isDisabled}
                                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[var(--tree-leaf)] focus:outline-none text-[#1C2045] disabled:opacity-60"
                                placeholder={t("uploads.login.passwordPlaceholder")}
                            />
                        </div>

                        <p className="text-gray-500 text-xs text-center">
                            {t("uploads.login.sessionHint")}
                        </p>

                        <div className="flex justify-center pt-2">
                            <SendButton
                                title={isDisabled ? t("uploads.login.submitting") : t("uploads.login.submit")}
                                onClick={handleSubmit}
                                disabled={!username.trim() || !password || isDisabled}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UploadLogin;
